import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as moment from 'moment';

import { CommonService } from '../../services/common.service';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Config } from '../../config';
import { DrilldownFormsService } from '../../services/drilldown-forms.service';

@Component({
  selector: 'vrm-index-override',
  templateUrl: './vrm-index-override.component.html',
  styleUrls: ['./vrm-index-override.component.scss'],
})
export class VrmIndexOverrideComponent implements OnInit {
  @Input() theIncident: any;
  @Input() allIncidents: any;
  @Output() vrmIndexOverrideChange: EventEmitter<any> = new EventEmitter<any>();

  overrideIncidentIndexForm: UntypedFormGroup;
  currentDate: string = moment().format(Config.momentDateFormatUs);
  permittedToOverride: boolean =
    this.cService.userProfile.userPermissions.permIndexOverride === this.cService.permLevels.override;

  constructor(public cService: CommonService, public dfsService: DrilldownFormsService) {}

  ngOnInit() {
    this.overrideIncidentIndexForm = new UntypedFormGroup({
      overrideVrmDriverIndex: new UntypedFormControl({
        value: this.theIncident.indexOverride,
        disabled: !this.permittedToOverride,
      }),

      overrideCompanyPoints: new UntypedFormControl({
        value: this.theIncident.indexPointsOverride,
        disabled: !this.permittedToOverride,
      }),

      overriddenCompanyPointsValue: new UntypedFormControl(
        {
          value: this.theIncident.indexManualPoints,
          disabled: !this.permittedToOverride || !this.theIncident.indexPointsOverride,
        },
        Validators.required
      ),

      overrideReason: new UntypedFormControl(
        {
          value: this.theIncident.indexPointsOverrideReason,
          disabled: !this.permittedToOverride || !this.theIncident.isOverridden,
        },
        Validators.required
      ),
    });

    this.overrideCompanyPointsControl.valueChanges.subscribe((isCompanyPointsOverrideChecked) => {
      isCompanyPointsOverrideChecked
        ? this.enableControl(this.overriddenCompanyPointsValueControl)
        : this.disableControl(this.overriddenCompanyPointsValueControl);

      isCompanyPointsOverrideChecked || this.overrideVrmDriverIndexControl.value
        ? this.enableControl(this.overrideReasonControl)
        : this.disableControl(this.overrideReasonControl);
    });

    this.overrideVrmDriverIndexControl.valueChanges.subscribe((vrmDriverIndexOverrideChecked) => {
      vrmDriverIndexOverrideChecked || this.overrideCompanyPointsControl.value
        ? this.enableControl(this.overrideReasonControl)
        : this.disableControl(this.overrideReasonControl);
    });
  }

  get overrideVrmDriverIndexControl() {
    return this.overrideIncidentIndexForm.get('overrideVrmDriverIndex');
  }

  get overrideCompanyPointsControl() {
    return this.overrideIncidentIndexForm.get('overrideCompanyPoints');
  }

  get overriddenCompanyPointsValueControl() {
    return this.overrideIncidentIndexForm.get('overriddenCompanyPointsValue');
  }

  get overrideReasonControl() {
    return this.overrideIncidentIndexForm.get('overrideReason');
  }

  overrideIncidentIndex(): void {
    this.overrideReasonControl.setValue(this.overrideReasonControl.value.trim());

    this.triggerValidation();

    if (!this.overrideIncidentIndexForm.valid) {
      return;
    }

    this.cService
      .commonPatch('driverindex/override/' + this.theIncident.incidentId, this.overrideIncidentIndexForm.value)
      .subscribe({
        next: (response: any) => {
          console.log('%c PATCH Response', 'color: salmon', response);
          this.updateTheIncident(response.data);
          this.theIncident.expandOverrideForm = false;
          this.vrmIndexOverrideChange.emit(response.data);
        },
        error: () => {
          const errorMessage = this.cService.readyTranslations.messagesAndWarnings.failedToProcessData;
        },
      });
  }

  toggleOverrideForm(): void {
    for (const summary of this.allIncidents) {
      for (const incident of summary.data.data) {
        if (incident !== this.theIncident) {
          incident.expandOverrideForm = false;
        } else {
          this.theIncident.expandOverrideForm = !this.theIncident.expandOverrideForm;
        }
      }
    }
  }

  private enableControl(formControl: AbstractControl): boolean {
    if (this.permittedToOverride && formControl.disabled) {
      formControl.enable();
      formControl.setValidators(Validators.required);
    }
    return true;
  }

  private disableControl(formControl: AbstractControl): boolean {
    if (formControl.enabled) {
      formControl.disable();
      formControl.clearValidators();
    }
    return true;
  }

  private triggerValidation(): void {
    this.overrideReasonControl.markAsDirty();
    this.overrideReasonControl.updateValueAndValidity();

    this.overriddenCompanyPointsValueControl.markAsDirty();
    this.overriddenCompanyPointsValueControl.updateValueAndValidity();
  }

  private updateTheIncident(updatedData) {
    for (const key of Object.keys(this.theIncident)) {
      this.theIncident[key] = updatedData[key];
    }
    this.overrideIncidentIndexForm.patchValue({
      overriddenCompanyPointsValue: this.theIncident.indexManualPoints,
      overrideReason: this.theIncident.indexPointsOverrideReason,
    });
  }
}
